import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { DEFAULT_TEACHERS, teacherSeedPayload } from '../constants/teachers';
import { DEFAULT_COORDINATORS, coordinatorSeedPayload } from '../constants/coordinators';
import { ensureStudentProfile } from '../api/adminApi';
import { findRegistryStudentByEmail, isStudentLoginEmail } from '../utils/studentLogin';

async function bootstrapCoordinatorProfile(firebaseUser) {
  const email = String(firebaseUser.email || '').trim().toLowerCase();
  if (!email) return null;
  const known = DEFAULT_COORDINATORS.find((c) => c.email.toLowerCase() === email);
  if (!known) return null;
  const seed = coordinatorSeedPayload(known);
  const profile = {
    email,
    name: seed.name || email,
    role: 'COORDINATOR',
    createdAt: new Date().toISOString(),
    bootstrappedFromSeed: true,
  };
  await setDoc(doc(db, 'users', firebaseUser.uid), profile);
  return { id: firebaseUser.uid, ...profile };
}

async function loadStudentRegistry() {
  const url = `${process.env.PUBLIC_URL || ''}/data/students-registry.json`;
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return [];
    const data = await res.json();
    return data.students || [];
  } catch {
    return [];
  }
}

async function bootstrapStudentProfile(firebaseUser) {
  const email = String(firebaseUser.email || '').trim().toLowerCase();
  if (!email || !isStudentLoginEmail(email)) return null;

  const registry = await loadStudentRegistry();
  const hit = findRegistryStudentByEmail(registry, email);
  if (!hit) return null;

  const admno = String(hit.admno || hit.studentId || '').trim();
  if (!admno) return null;

  try {
    await ensureStudentProfile(hit);
    const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  } catch {
    /* fall through to client-side create */
  }

  const profile = {
    email,
    name: hit.studentName || hit.name || admno,
    role: 'STUDENT',
    admno,
    class: hit.class || '',
    studentCsvId: hit.studentCsvId ? String(hit.studentCsvId) : null,
    createdAt: new Date().toISOString(),
    bootstrappedFromRegistry: true,
  };

  await setDoc(doc(db, 'users', firebaseUser.uid), profile, { merge: true });
  return { id: firebaseUser.uid, ...profile };
}

async function syncStudentProfileIfNeeded(firebaseUser) {
  if (!isStudentLoginEmail(firebaseUser.email)) return;
  const registry = await loadStudentRegistry();
  const hit = findRegistryStudentByEmail(registry, firebaseUser.email);
  if (!hit) return;
  try {
    await ensureStudentProfile(hit);
  } catch {
    /* client bootstrap may still run */
  }
}

async function bootstrapTeacherProfile(firebaseUser) {
  const email = String(firebaseUser.email || '').trim().toLowerCase();
  if (!email || isStudentLoginEmail(email)) return null;
  const seedRef = doc(db, 'teacherSeeds', email);
  const seedSnap = await getDoc(seedRef);
  let seed = seedSnap.exists() ? seedSnap.data() : null;
  if (!seed) {
    const known = DEFAULT_TEACHERS.find((t) => t.email.toLowerCase() === email);
    if (known) seed = teacherSeedPayload(known);
  }
  if (!seed) return null;
  const profile = {
    email,
    name: seed.name || email,
    role: 'TEACHER',
    allClasses: seed.allClasses !== false,
    assignedClasses: seed.assignedClasses || [],
    createdAt: new Date().toISOString(),
    bootstrappedFromSeed: true,
  };
  await setDoc(doc(db, 'users', firebaseUser.uid), profile);
  if (seedSnap.exists()) await deleteDoc(seedRef);
  return { id: firebaseUser.uid, ...profile };
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return undefined;
    }
    return onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setError('');
      setProfile(null);
      if (!firebaseUser || !db) {
        setLoading(false);
        return;
      }
      try {
        await syncStudentProfileIfNeeded(firebaseUser);
        const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          const needsStudentFix =
            isStudentLoginEmail(firebaseUser.email) &&
            (data.role !== 'STUDENT' || !data.admno || !data.name);
          if (needsStudentFix) {
            const fixed = await bootstrapStudentProfile(firebaseUser);
            setProfile(fixed || { id: snap.id, ...data });
          } else {
            setProfile({ id: snap.id, ...data });
          }
        } else {
          const bootstrapped =
            (await bootstrapCoordinatorProfile(firebaseUser)) ||
            (await bootstrapTeacherProfile(firebaseUser)) ||
            (await bootstrapStudentProfile(firebaseUser));
          if (bootstrapped) {
            setProfile(bootstrapped);
          } else if (isStudentLoginEmail(firebaseUser.email)) {
            setError('Student login not found in school registry. Check login id and admission number, or contact the school office.');
          } else {
            setError('No user profile found. Ask admin to create your account.');
          }
        }
      } catch (err) {
        setError(err.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const value = useMemo(
    () => ({ user, profile, loading, error, role: profile?.role || null }),
    [user, profile, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
